import { startOfToday } from 'date-fns';
import * as Status from '../../../api/Advisors/types/Status';
import { SpecializationId } from '../../../api/specializations';
import * as Either from '../../../utils/Either';
import * as V from '../../../utils/FormValue';
import { invalid, verifying } from '../../../utils/FormValue';
import { isEmail } from '../../../utils/String/Email';
import { isPhone } from '../../../utils/String/Phone';
import * as S from '../../../utils/String/Sentence';
import { Item } from './types/Item';
import { length } from '../../../utils/wysiwyg';
import { lengthField } from '../../../utils/form';

type SentenceValidator<N extends number> =
  | V.Valid<S.Sentence<N>>
  | V.Invalid<string, string>;

const required =
  <T, X extends T, E>(validator: (t: T) => V.Valid<X> | V.Invalid<E, T>) =>
  (v: T | undefined): V.Invalid<string | E, undefined | T> | V.Valid<X> =>
    v !== undefined && (v as unknown) !== ''
      ? validator(v)
      : V.invalid('Required', undefined);

const optional =
  <T, X extends T, E>(validator: (t: T) => V.Valid<X> | V.Invalid<E, T>) =>
  (v: T | undefined): V.Invalid<E, T> | V.Valid<X | undefined> =>
    v === undefined || (v as unknown) === ''
      ? V.valid(undefined)
      : validator(v);

// region Validators
const sentence =
  <N extends number>(n: N) =>
  (s: string): SentenceValidator<N> => {
    return Either.apply<string, S.Sentence<N>, SentenceValidator<N>>(
      () => V.invalid(`String should be less then ${n} characters`, s),
      V.valid,
      S.fromString(n)(s),
    );
  };

const number = (min: number, max: number) => (n: number) => {
  return n >= min && n <= max
    ? V.valid(n)
    : V.invalid(
        `Number should be between ${min} and ${max}` as unknown as number,
        n,
      );
};

const status = (s: Status.Status) => V.valid(s);

const specializations = (s: SpecializationId[]) => V.valid(s);

const birthDate = (d: Date) =>
  d < startOfToday()
    ? V.valid(d)
    : V.invalid('It seems you were born in future', d);

const phone = (p: string) =>
  isPhone(p) ? V.valid(p) : V.invalid('Invalid phone number', p);

const wysiwyg =
  <N extends number>(htmlMax: N, textMax: N) =>
  (s: string): SentenceValidator<N> => {
    if (s.length > htmlMax) {
      return V.invalid(
        `String HTML should be less then ${htmlMax} characters`,
        s,
      );
    }
    if (length(s) > textMax) {
      return V.invalid(
        `String text should be less then ${textMax} characters`,
        s,
      );
    }

    return Either.apply<string, S.Sentence<N>, SentenceValidator<N>>(
      () => V.invalid(`String should be less then ${htmlMax} characters`, s),
      V.valid,
      S.fromString(htmlMax)(s),
    );
  };
// endregion

type SyncValidators = {
  [K in keyof Omit<Item, 'email' | 'firstName' | 'lastName'>]: (
    v: V.ValueType<Item[K]>,
  ) => V.SubmittedValue<Item[K]>;
};

type AsyncValidator<T extends keyof Item> = (
  v: V.ValueType<Item[T]>,
) => V.SubmittedAsyncValue<Item[T]>;

interface Validators extends SyncValidators {
  firstName: AsyncValidator<'firstName'>;
  lastName: AsyncValidator<'lastName'>;
  email: AsyncValidator<'email'>;
}

export const validators: Validators = {
  firstName: (v) => (v ? verifying(v) : invalid('required', v)),
  lastName: (v) => (v ? verifying(v) : invalid('required', v)),
  email: (v) => (v && isEmail(v) ? verifying(v) : invalid('required', v)),
  country: optional(sentence(250)),
  state: optional(sentence(250)),
  city: optional(sentence(250)),
  linkedIn: optional(sentence(250)),
  short: required(
    wysiwyg(lengthField.wysiwygMaxLength, lengthField.wysiwygDisplayLimit) as any,
  ),
  long: required(
    wysiwyg(lengthField.wysiwygMaxLength, lengthField.wysiwygDisplayLimit) as any,
  ),
  address: optional(sentence(1024)),
  phone: optional(phone),
  logo: optional(sentence(1024)),
  zip: optional(sentence(12)),
  status: status,
  birthDate: optional(birthDate),
  specializations: specializations,
  rate: optional(number(0, 999)),
};
